<template>
  <ButtonGroup
    class="toggle-switch-square"
    :class="{ 'is-disabled': props.disabled }"
  >
    <Button
      type="button"
      class="toggle-switch-square__button-left"
      :class="{ 'is-not-checked': isChecked }"
      @click="handleClick(false)"
    >
      <div
        class="button-text"
        :class="{ 'center-adjust': props.reserveIconSpace && isChecked }"
      >
        <template v-if="props.reserveIconSpace">
          <CheckmarkIcon :class="{ 'invisible-icon': isChecked }" />
        </template>
        <template v-else>
          <CheckmarkIcon v-if="!isChecked" />
        </template>
        {{ props.labelLeft }}
      </div>
    </Button>
    <Button
      type="button"
      class="toggle-switch-square__button-right"
      :class="{ 'is-not-checked': !isChecked }"
      @click="handleClick(true)"
    >
      <div
        class="button-text"
        :class="{ 'center-adjust': props.reserveIconSpace && !isChecked }"
      >
        <template v-if="props.reserveIconSpace">
          <CheckmarkIcon :class="{ 'invisible-icon': !isChecked }" />
        </template>
        <template v-else>
          <CheckmarkIcon v-if="isChecked" />
        </template>
        {{ props.labelRight }}
      </div>
    </Button>
  </ButtonGroup>
</template>

<script setup lang="ts">

import {ButtonGroup, Button} from '@progress/kendo-vue-buttons'
import CheckmarkIcon from '~/assets/icons/navigation_check.svg?component'

const emit = defineEmits(['change-value'])

const props = defineProps({
  labelLeft: {
    type: String,
    default: ''
  },
  labelRight: {
    type: String,
    default: ''
  },
  isChecked: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  reserveIconSpace: {
    type: Boolean,
    default: false
  }
})

const handleClick = (value: boolean) => {
  if (value === props.isChecked || props.disabled) {
    return
  }
  emit('change-value', value)
}

</script>

<style scoped lang="scss">
.toggle-switch-square {
  display: flex;

  &.is-disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }

  &__button-left,
  &__button-right {
    min-width: auto;
    width: 50%;
    min-height: 2rem;
    max-height: 2rem;
    -webkit-tap-highlight-color: $color__primary;

    .button-text {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    svg {
      width: 1rem;
      height: 1rem;
      margin-right: 0.15rem;
    }
  }

  .is-not-checked {
    background-color: $color__fst__white;
    color: $color__fst__primary;
    border: 0.063rem solid $color__fst__gray__030 !important;
    box-sizing: border-box;
  }

  @include breakpoint-down($md) {
    max-width: 8.5rem;
  }
}

.invisible-icon {
  opacity: 0;
  visibility: hidden;
}

/* Shift text left by half icon width when placeholder icon hidden to keep visual centering */
.center-adjust {
  margin-left: -0.5rem; /* half of icon (1rem) minus typical spacing; tweak if needed */
}

/* Remove sticky hover effect on touch devices (Chrome mobile emulator, real touch screens) */
@media (hover: none) {
  .toggle-switch-square__button-left:hover,
  .toggle-switch-square__button-right:hover,
  .toggle-switch-square__button-left.k-hover,
  .toggle-switch-square__button-right.k-hover {
    /* Neutralize library hover color on touch */
    background-color: $color__primary !important;
    color: $color__white !important;
  }
}
</style>
